import * as React from "react";

import { assertNever } from "@impero-com/common/utils/obj";

import {
    StringInput,
    StringInputProps,
} from "components/form/legacyFields/stringInput";
import * as CT from "org/risks/content/types";

import { mapForm, simpleMappers, WrappedFormUtils } from "../../../utils/form";
import * as U from "../content/utils";

import * as T from "./types";

type TextInputMetadataFieldProps<ROOT> = T.SharedProperties<
    string,
    ROOT,
    CT.TextInputInstance
>;

export default class TextInputMetadataField<ROOT> extends React.PureComponent<
    TextInputMetadataFieldProps<ROOT>
> {
    render = () => {
        const { id, label, instance } = this.props;
        const baseProps = {
            id,
            label,
        };
        let props: StringInputProps<string, ROOT>;
        switch (this.props.type) {
            case "CREATE":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    initialValue: undefined,
                    form: mapInstanceFormToStringForm(
                        id,
                        this.props.form,
                        instance
                    ),
                };
                break;
            case "EDIT":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: this.props.instance.value,
                    onChange: this.handleOnChange,
                    validationError: this.props.validationError,
                };
                break;
            case "READ_ONLY":
                props = {
                    ...baseProps,
                    type: this.props.type,
                    value: this.props.instance.value,
                };
                break;
            default:
                return assertNever(this.props);
        }
        return <StringInput {...props} markdown={true} />;
    };

    private handleOnChange = (value: string | undefined) => {
        const { instance } = this.props;
        if (this.props.type === "EDIT") {
            return this.props.onChangeAsync(
                Promise.resolve(value),
                (_prev, val) =>
                    U.extractMetadataInstanceValue(
                        stringToInstance(val, instance)
                    )
            );
        }
        return Promise.reject(
            new Error("Cannot change value in non-edit mode")
        );
    };
}

function instanceToString(instance: CT.MetadataInstance): string | undefined {
    if (instance.type !== "TextInputInstance") {
        throw new Error("Expected textinput instance, got " + instance.type);
    }
    return instance.value;
}

function stringToInstance(
    stringValue: string | undefined,
    oldInstance: CT.MetadataInstance
): CT.MetadataInstance {
    if (oldInstance.type !== "TextInputInstance") {
        throw new Error("Expected textinput instance, got " + oldInstance.type);
    }
    return {
        ...oldInstance,
        value: stringValue !== "" ? stringValue : undefined,
    };
}

function mapInstanceFormToStringForm<ROOT>(
    id: string,
    instanceForm: WrappedFormUtils<
        {
            [x: string]: CT.MetadataInstance;
        },
        ROOT
    >,
    instance: CT.MetadataInstance
): WrappedFormUtils<
    {
        [x: string]: string;
    },
    ROOT
> {
    const mapper = simpleMappers<
        string,
        CT.MetadataInstance,
        string | undefined,
        ROOT
    >(id, instanceToString, (value) => stringToInstance(value, instance));
    return mapForm<Record<string, string>>()(instanceForm, mapper());
}
